import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import url from '../constants/config';
import Loader from '../shared/displayComponents/loader/Loader';

const getAuthHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem('authorization')}`,
});

const STATUS_LABELS = {
  waiting_athlete: 'Oczekuje sportowca',
  waiting_university: 'Oczekuje uczelni',
  waiting_playsport: 'Oczekuje PLAYSPORT',
  to_verify: 'Do weryfikacji',
  on_hold: 'Wstrzymane',
};

const TASK_STATUS_STYLES = {
  not_started:        { bg: '#f1f5f9', color: '#64748b', label: 'Nie rozpoczęto' },
  in_progress:        { bg: '#dbeafe', color: '#1d4ed8', label: 'W trakcie' },
  waiting:            { bg: '#fef9c3', color: '#92400e', label: 'Oczekuje' },
  waiting_athlete:    { bg: '#fef3c7', color: '#b45309', label: 'Czeka sportowiec' },
  waiting_university: { bg: '#e0f2fe', color: '#0369a1', label: 'Czeka uczelnia' },
  waiting_playsport:  { bg: '#ede9fe', color: '#7c3aed', label: 'Czeka PLAYSPORT' },
  to_verify:          { bg: '#d1fae5', color: '#059669', label: 'Do weryfikacji' },
  on_hold:            { bg: '#f1f5f9', color: '#475569', label: 'Wstrzymano' },
  done:               { bg: '#dcfce7', color: '#15803d', label: 'Ukończono' },
};

const STATUS_COLORS = {
  waiting_athlete: { bg: '#fef3c7', border: '#fde68a', text: '#92400e', count: '#b45309' },
  waiting_university: { bg: '#e0f2fe', border: '#bae6fd', text: '#0c4a6e', count: '#0369a1' },
  waiting_playsport: { bg: '#ede9fe', border: '#ddd6fe', text: '#4c1d95', count: '#7c3aed' },
  to_verify: { bg: '#d1fae5', border: '#a7f3d0', text: '#064e3b', count: '#059669' },
  on_hold: { bg: '#f1f5f9', border: '#e2e8f0', text: '#334155', count: '#475569' },
};

const thStyle = {
  padding: '10px 12px',
  textAlign: 'left',
  fontWeight: 600,
  color: '#475569',
  whiteSpace: 'nowrap',
  fontSize: '13px',
};

const tdStyle = {
  padding: '10px 12px',
  verticalAlign: 'middle',
  fontSize: '13px',
};

const PRESET_HOURS = [24, 48, 72];

const AdminOpsDashboard = () => {
  const history = useHistory();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedAssistants, setExpandedAssistants] = useState({});

  // Alert-threshold settings state
  const [alertHours, setAlertHours] = useState(48);
  const [alertHoursInput, setAlertHoursInput] = useState('48');
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsFeedback, setSettingsFeedback] = useState(null); // { type: 'success'|'error', msg }

  useEffect(() => {
    axios
      .get(`${url}/admin/ops-dashboard`, { headers: getAuthHeader() })
      .then(res => setData(res.data))
      .catch(() => setError('Nie udało się załadować danych.'))
      .finally(() => setLoading(false));

    // Load current alert settings
    axios
      .get(`${url}/admin/settings`, { headers: getAuthHeader() })
      .then(res => {
        const h = res.data.inactivityAlertHours;
        setAlertHours(h);
        setAlertHoursInput(String(h));
      })
      .catch(() => {}); // non-critical
  }, []);

  const saveAlertSettings = () => {
    const hours = parseInt(alertHoursInput, 10);
    if (!hours || hours < 1) {
      setSettingsFeedback({ type: 'error', msg: 'Podaj liczbę godzin większą od 0.' });
      return;
    }
    setSettingsSaving(true);
    setSettingsFeedback(null);
    axios
      .patch(`${url}/admin/settings`, { inactivityAlertHours: hours }, { headers: getAuthHeader() })
      .then(() => {
        setAlertHours(hours);
        setSettingsFeedback({ type: 'success', msg: 'Zapisano.' });
        setTimeout(() => setSettingsFeedback(null), 3000);
      })
      .catch(() => setSettingsFeedback({ type: 'error', msg: 'Błąd zapisu. Spróbuj ponownie.' }))
      .finally(() => setSettingsSaving(false));
  };

  const toggleAssistant = email => {
    setExpandedAssistants(prev => ({ ...prev, [email]: !prev[email] }));
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return <div style={{ padding: '32px', color: '#ef4444' }}>{error}</div>;
  }

  const { statusCounts, staleTasks, assistantBreakdown } = data;

  return (
    <div style={{ padding: '24px 32px', maxWidth: '1100px' }}>
      <h2 style={{ margin: '0 0 24px', color: '#1e293b', fontSize: '22px', fontWeight: 700 }}>
        Dashboard Operacyjny
      </h2>

      {/* Status cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: '12px',
          marginBottom: '32px',
        }}
      >
        {Object.keys(STATUS_LABELS).map(key => {
          const colors = STATUS_COLORS[key];
          return (
            <div
              key={key}
              style={{
                background: colors.bg,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  fontSize: '28px',
                  fontWeight: 700,
                  color: colors.count,
                  lineHeight: 1,
                  marginBottom: '6px',
                }}
              >
                {statusCounts[key] || 0}
              </div>
              <div style={{ fontSize: '11px', color: colors.text, fontWeight: 600, lineHeight: 1.3 }}>
                {STATUS_LABELS[key]}
              </div>
            </div>
          );
        })}
      </div>

      {/* Assistant breakdown */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ margin: '0 0 12px', color: '#1e293b', fontSize: '16px', fontWeight: 700 }}>
          Praca Asystentów
        </h3>
        {assistantBreakdown.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: '14px' }}>Brak asystentów.</div>
        ) : (
          <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
            {assistantBreakdown.map(a => {
              const isExpanded = expandedAssistants[a.assistantEmail];
              const totalTasks = a.players.reduce((s, p) => s + p.totalAssigned, 0);
              const doneTasks = a.players.reduce((s, p) => s + p.doneTasks, 0);
              const pendingTasks = totalTasks - doneTasks;
              return (
                <div key={a.assistantEmail} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  {/* Assistant header row */}
                  <button
                    type="button"
                    onClick={() => toggleAssistant(a.assistantEmail)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      padding: '12px 16px',
                      background: isExpanded ? '#f8fafc' : '#fff',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, fontSize: '14px', color: '#1e293b' }}>
                        {a.firstName} {a.lastName}
                      </span>
                      <span style={{ fontSize: '12px', color: '#94a3b8' }}>{a.assistantEmail}</span>
                      <span
                        style={{
                          background: '#e0f2fe',
                          color: '#0369a1',
                          padding: '1px 8px',
                          borderRadius: '10px',
                          fontSize: '11px',
                          fontWeight: 600,
                        }}
                      >
                        {a.players.length} zawodnik{a.players.length !== 1 ? 'ów' : ''}
                      </span>
                      {pendingTasks > 0 && (
                        <span
                          style={{
                            background: '#fef3c7',
                            color: '#b45309',
                            padding: '1px 8px',
                            borderRadius: '10px',
                            fontSize: '11px',
                            fontWeight: 600,
                          }}
                        >
                          {pendingTasks} w toku
                        </span>
                      )}
                      {doneTasks > 0 && (
                        <span
                          style={{
                            background: '#dcfce7',
                            color: '#15803d',
                            padding: '1px 8px',
                            borderRadius: '10px',
                            fontSize: '11px',
                            fontWeight: 600,
                          }}
                        >
                          {doneTasks} ukończonych
                        </span>
                      )}
                    </div>
                    <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`} style={{ color: '#94a3b8' }} />
                  </button>

                  {/* Expanded: players + their individual tasks */}
                  {isExpanded && (
                    <div style={{ background: '#f8fafc', paddingBottom: '4px' }}>
                      {a.players.length === 0 ? (
                        <div style={{ color: '#94a3b8', fontSize: '13px', padding: '12px 32px' }}>
                          Brak przypisanych zadań.
                        </div>
                      ) : (
                        a.players.map(p => (
                          <div
                            key={p.playerId}
                            style={{
                              borderBottom: '1px solid #e2e8f0',
                              padding: '10px 16px 10px 32px',
                            }}
                          >
                            {/* Player name — clickable */}
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                marginBottom: p.tasks.length > 0 ? '8px' : '0',
                              }}
                            >
                              <span
                                role="button"
                                tabIndex={0}
                                style={{
                                  fontWeight: 600,
                                  fontSize: '13px',
                                  color: '#2e496b',
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                  textUnderlineOffset: '2px',
                                }}
                                onClick={() =>
                                  history.push(`/available-players/${p.playerId}?tab=checklist`)
                                }
                                onKeyDown={e => {
                                  if (e.key === 'Enter')
                                    history.push(`/available-players/${p.playerId}?tab=checklist`);
                                }}
                              >
                                {p.playerName}
                              </span>
                              <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                                {p.totalAssigned} zadań
                                {p.doneTasks > 0 && ` · ${p.doneTasks} ukończonych`}
                              </span>
                            </div>

                            {/* Individual tasks */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              {p.tasks.map(t => {
                                const style = TASK_STATUS_STYLES[t.status] || TASK_STATUS_STYLES.not_started;
                                return (
                                  <div
                                    key={t.taskId}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      fontSize: '12px',
                                    }}
                                  >
                                    {/* Status badge */}
                                    <span
                                      style={{
                                        flexShrink: 0,
                                        background: style.bg,
                                        color: style.color,
                                        padding: '1px 7px',
                                        borderRadius: '8px',
                                        fontWeight: 600,
                                        fontSize: '11px',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {style.label}
                                    </span>
                                    {/* Task label */}
                                    <span style={{ color: '#334155', flex: 1 }}>{t.label}</span>
                                    {/* Days without activity */}
                                    {t.daysSinceUpdate != null && t.status !== 'done' && (
                                      <span
                                        style={{
                                          flexShrink: 0,
                                          color:
                                            t.daysSinceUpdate > 7
                                              ? '#dc2626'
                                              : t.daysSinceUpdate > 2
                                              ? '#f59e0b'
                                              : '#94a3b8',
                                          fontWeight: t.daysSinceUpdate > 2 ? 600 : 400,
                                          fontSize: '11px',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {t.daysSinceUpdate === 0
                                          ? 'dziś'
                                          : `${t.daysSinceUpdate}d temu`}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Alert threshold settings */}
      <div
        style={{
          marginBottom: '32px',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          padding: '20px 24px',
          background: '#fff',
        }}
      >
        <h3 style={{ margin: '0 0 4px', color: '#1e293b', fontSize: '16px', fontWeight: 700 }}>
          Ustawienia alertów — brak aktywności
        </h3>
        <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: '13px' }}>
          Alert jest wysyłany do admina, gdy przypisany task nie ma aktywności przez podaną liczbę godzin.
          Aktualne ustawienie: <strong>{alertHours}h</strong>.
        </p>

        {/* Preset buttons */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          {PRESET_HOURS.map(h => (
            <button
              key={h}
              type="button"
              onClick={() => setAlertHoursInput(String(h))}
              style={{
                padding: '6px 16px',
                borderRadius: '6px',
                border: `1px solid ${alertHoursInput === String(h) ? '#2e496b' : '#e2e8f0'}`,
                background: alertHoursInput === String(h) ? '#2e496b' : '#f8fafc',
                color: alertHoursInput === String(h) ? '#fff' : '#475569',
                fontWeight: 600,
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              {h}h
            </button>
          ))}

          {/* Custom input */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <input
              type="number"
              min="1"
              value={alertHoursInput}
              onChange={e => setAlertHoursInput(e.target.value)}
              style={{
                width: '80px',
                padding: '6px 10px',
                borderRadius: '6px',
                border: '1px solid #e2e8f0',
                fontSize: '13px',
                color: '#1e293b',
              }}
            />
            <span style={{ fontSize: '13px', color: '#64748b' }}>godz.</span>
          </div>

          <button
            type="button"
            onClick={saveAlertSettings}
            disabled={settingsSaving}
            style={{
              padding: '6px 20px',
              borderRadius: '6px',
              border: 'none',
              background: settingsSaving ? '#94a3b8' : '#2e496b',
              color: '#fff',
              fontWeight: 600,
              fontSize: '13px',
              cursor: settingsSaving ? 'not-allowed' : 'pointer',
            }}
          >
            {settingsSaving ? 'Zapisywanie…' : 'Zapisz'}
          </button>

          {settingsFeedback && (
            <span
              style={{
                fontSize: '13px',
                fontWeight: 600,
                color: settingsFeedback.type === 'success' ? '#059669' : '#dc2626',
              }}
            >
              {settingsFeedback.msg}
            </span>
          )}
        </div>
      </div>

      {/* Stale tasks */}
      <div>
        <h3 style={{ margin: '0 0 12px', color: '#1e293b', fontSize: '16px', fontWeight: 700 }}>
          Top 10 Tasków bez ruchu
        </h3>
        {staleTasks.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: '14px' }}>Brak stagnujących zadań.</div>
        ) : (
          <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0', background: '#f8fafc' }}>
                  <th style={thStyle}>Zadanie</th>
                  <th style={thStyle}>Zawodnik</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Asystent</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Dni bez ruchu</th>
                </tr>
              </thead>
              <tbody>
                {staleTasks.map(t => (
                  <tr
                    key={t._id}
                    onClick={() => history.push(`/available-players/${t.playerId}?tab=checklist`)}
                    style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = ''; }}
                  >
                    <td style={{ ...tdStyle, fontWeight: 500, color: '#1e293b', maxWidth: '240px' }}>
                      {t.label || '—'}
                    </td>
                    <td style={tdStyle}>{t.playerName}</td>
                    <td style={tdStyle}>
                      <span
                        style={{
                          background: '#f1f5f9',
                          color: '#475569',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          fontSize: '11px',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t.status ? t.status.replace(/_/g, ' ') : '—'}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, color: '#64748b' }}>
                      {t.assignedToAssistant || <span style={{ color: '#cbd5e1' }}>—</span>}
                    </td>
                    <td
                      style={{
                        ...tdStyle,
                        textAlign: 'right',
                        fontWeight: 600,
                        color: t.daysSinceUpdate > 30 ? '#dc2626' : t.daysSinceUpdate > 7 ? '#f59e0b' : '#64748b',
                      }}
                    >
                      {t.daysSinceUpdate != null ? t.daysSinceUpdate : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOpsDashboard;
